<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import {
  CsButton,
  CsIcon,
  CsInput,
  CsModal,
  CsModalContent,
  CsModalFooter,
  CsModalHeader,
} from '../../index';
import { toolbarButtonItemRecipe } from './toolbarRecipe';

/**
 * @typedef {object} LinkProps
 * @property {string} [value] - The value of the link
 * @property {boolean} [isActive=false] - Whether the link is currently active
 * @property {boolean} [isDisabled=false] - Whether the link is disabled
 * @property {boolean} [isCanRemoveLink=false] - Whether the link can be removed
 * @property {('default'|'inverse')} [variant='default'] - Color variant for the icon
 */

/** @type {LinkProps} */
const props = defineProps({
  value: {
    type: [String],
    required: true,
  },
  isActive: {
    type: Boolean,
    required: true,
  },
  isDisabled: {
    type: Boolean,
    required: true,
  },
  isCanRemoveLink: {
    type: Boolean,
    required: true,
  },
  variant: {
    type: String,
    default: 'default',
  },
});

const emit = defineEmits([
  'input',
  'open',
  'close',
  'click-remove-link',
  'click-insert-link',
]);

const inputRef = ref();
const isModalOpen = ref(false);
const innerValue = ref('');

function onClickRemoveLink() {
  emit('click-remove-link');

  onCloseModal();
}

function onClickInsertLink() {
  emit('click-insert-link', innerValue.value);

  onCloseModal();
}

function onOpenModal() {
  emit('open');

  isModalOpen.value = true;

  nextTick(() => {
    setTimeout(() => {
      innerValue.value = props.value;
      inputRef.value?.focus();
    }, 100);
  });
}

function onCloseModal() {
  isModalOpen.value = false;
  emit('close');

  // Reset the inner value
  innerValue.value = '';
}

watch(
  () => props.value,
  newValue => {
    innerValue.value = newValue;
  }
);
</script>

<template>
  <div class="flex items-center">
    <button
      v-cs-tooltip="'Insert link'"
      data-component="CsEditorToolbarLink"
      :class="toolbarButtonItemRecipe(props)"
      :disabled="props.isDisabled"
      @click="onOpenModal"
    >
      <CsIcon name="link" :class="{ 'opacity-50': props.isDisabled }" />
    </button>

    <CsModal :is-open="isModalOpen" size="lg" @close="isModalOpen = false">
      <CsModalHeader>
        {{ props.isCanRemoveLink ? 'Edit link' : 'Insert link' }}
      </CsModalHeader>
      <CsModalContent>
        <CsInput
          ref="inputRef"
          v-model="innerValue"
          label="Link to"
          :is-clearable="false"
          placeholder="Enter URL (e.g., https://example.com)"
          class="w-full"
        />
      </CsModalContent>

      <CsModalFooter class="!justify-between">
        <div>
          <CsButton
            v-if="props.isCanRemoveLink"
            variant="critical"
            @click="onClickRemoveLink"
          >
            Remove link
          </CsButton>
        </div>

        <div class="flex items-center gap-2">
          <CsButton @click="onCloseModal"> Cancel </CsButton>
          <CsButton
            variant="primary"
            :is-disabled="!innerValue.length"
            @click="onClickInsertLink"
          >
            Insert link
          </CsButton>
        </div>
      </CsModalFooter>
    </CsModal>
  </div>
</template>
